/**
 * Shared design tokens for the public landing experience.
 *
 * What: Exposes a small, stable set of landing-only colors, radii, shadows, and spacing presets.
 * Why: Keeps landing sections visually consistent and avoids scattered magic values across components.
 * When: Update when the landing brand palette or layout rhythm changes (or when migrating tokens into a global theme).
 */

export const colors = {
  // H4 "Cobalt + Orange" palette
  // Brand: deep cobalt blue. Action: vivid orange for CTAs.
  // Base: ice-blue tinted white. Gender-neutral, Gen Z-friendly.
  background: '#F5F7FC',
  backgroundAlt: '#EDF0F9',
  paper: '#FFFFFF',

  ink: '#1A1E30',
  inkMuted: '#6A7088',
  inkSubtle: '#7A8099',

  border: '#E2E6F2',
  accentCobalt: '#3A56D4',
  accentCobaltDark: '#2A3EB1',
  accentCobaltLight: '#5A76E4',
  /** Gradient mid-stop only (§4) — the brighter inner cobalt of the brand CTA gradient. */
  accentCobaltBright: '#4361EE',
  accentOrange: '#F06830',
  accentOrangeDark: '#B84A1C',
  accentOrangeLight: '#FF8A5C',
  accentAmber: '#F0A880',
  accentAmberDark: '#D18B63',
  accentGreen: '#2EA366',
  accentGreenDark: '#1E7A4A',
  accentBrick: '#D63847',
  /** Hero product-card illustration accents only — never a UI role (§4). */
  accentGold: '#FBD36B',
  accentGoldDark: '#F0A030',

  // Tinted card/icon washes — the palest step of each accent, for chip and
  // icon-tile backgrounds that must not read as a filled state.
  washGreen: '#EAF6F0',
  washCobalt: '#E9EDFB',
  washAmber: '#FDF1E8',

  // Transitional legacy landing palette (dark)
  night: '#0b1020',
  onNight: '#FFFFFF',
} as const;

/**
 * Corner radii, as CSS `px` strings — never bare numbers.
 *
 * MUI's `sx` treats a numeric `borderRadius` as a MULTIPLIER of
 * `theme.shape.borderRadius`, not as pixels, so `sx={{ borderRadius: 24 }}`
 * renders 24 × shape (96px against MUI's default 4) rather than 24px. A `px`
 * string passes through the `sx` transform untouched and is equally valid in
 * plain emotion objects and `styleOverrides`, so one token is correct in every
 * context. Guarded by `landingTokens.test.ts`.
 */
export const radii = {
  sm: '10px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  /** Pill/capsule shape for chips and tags; matches article page category chip. */
  pill: '9999px',
} as const;

export const shadows = {
  soft: '0 12px 32px rgba(0,0,0,0.08)',
  lift: '0 18px 60px rgba(0,0,0,0.12)',
  /** Elevated hero/feature card — the deepest lift on a marketing surface. */
  hero: '0 24px 60px rgba(12,18,45,.14)',
  /** Standard card at rest inside a hero card. */
  card: '0 8px 24px rgba(12,18,45,.09)',
  /** Barely-there separation for chips and inline tiles. */
  hairline: '0 2px 6px rgba(12,18,45,.06)',
} as const;

export const sectionSpacing = {
  py: { xs: 6, sm: 8, md: 12 },
  pyTight: { xs: 4, sm: 5, md: 8 },
  mb: { xs: 4, sm: 6, md: 8 },
  mbLoose: { xs: 6, sm: 8, md: 12 },
  outerStack: { xs: 3.5, sm: 4, md: 5 },
  outerStackLoose: { xs: 4, sm: 5, md: 6 },
} as const;

export const containerPadding = {
  px: { xs: 3, sm: 4, md: 5 },
} as const;
